import { useState, useRef } from "react";
import { RecaptchaWidget } from "@/components/ui/RecaptchaWidget";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/lib/language";
import { useSubmitTestimonial } from "@/lib/hooks";
import { TestimonialForm } from "@/lib/types";
import { Star, MessageSquare, AlertCircle, CheckCircle, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { ComponentProps } from "react";

// Custom RTL-aware DialogContent
function RTLDialogContent({
  className,
  children,
  dir,
  t,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  dir: "ltr" | "rtl";
  t: (key: string) => string;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            dir === "rtl" ? "left-4" : "right-4"
          )}
        >
          <X />
          <span className="sr-only">{t("close")}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function TestimonialDialog() {
  const { t, language, dir } = useLanguage();
  const submitTestimonialMutation = useSubmitTestimonial();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const recaptchaRef = useRef<{ reset: () => void }>(null);

  const handleOpenPrivacyPolicy = () => {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    const hasLanguagePrefix =
      firstSegment === "he" || firstSegment === "en" || firstSegment === "fr";

    const targetPath = hasLanguagePrefix
      ? `/${firstSegment}/privacy-policy`
      : `/${language}/privacy-policy`;

    window.open(targetPath, "_blank", "noopener,noreferrer");
  };

  const handleOpenTermsOfService = () => {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    const hasLanguagePrefix =
      firstSegment === "he" || firstSegment === "en" || firstSegment === "fr";

    const targetPath = hasLanguagePrefix
      ? `/${firstSegment}/terms-of-use`
      : `/${language}/terms-of-use`;

    window.open(targetPath, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      return;
    }

    if (!privacyAgreed) {
      setShowPrivacyError(true);
      return;
    }

    setShowPrivacyError(false);

    const testimonial: TestimonialForm = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      rating,
      language,
      captchaToken,
    };

    submitTestimonialMutation.mutate(testimonial, {
      onSuccess: () => {
        // Reset form
        setFormData({ name: "", email: "", message: "" });
        setRating(5);
        setPrivacyAgreed(false);
        setShowPrivacyError(false);
        setCaptchaToken("");
        recaptchaRef.current?.reset();

        // Close dialog after delay
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      },
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mt-4">
          <MessageSquare
            size={16}
            className={cn(dir === "rtl" ? "ml-2" : "mr-2")}
          />
          {t("shareTestimonial")}
        </Button>
      </DialogTrigger>
      <RTLDialogContent className="sm:max-w-[600px]" dir={dir} t={t}>
        <DialogHeader
          className={cn(
            "flex flex-col gap-2",
            dir === "rtl" ? "text-right" : "text-left"
          )}
        >
          <DialogTitle>{t("shareTestimonial")}</DialogTitle>
          <DialogDescription>{t("testimonialDescription")}</DialogDescription>
        </DialogHeader>

        {submitTestimonialMutation.isSuccess && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {t("testimonialSubmitted")}
            </AlertDescription>
          </Alert>
        )}

        {submitTestimonialMutation.isError && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {t("testimonialError")}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6" dir={dir}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("testimonialName")}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder={t("namePlaceholder")}
                required
                disabled={
                  submitTestimonialMutation.isPending ||
                  submitTestimonialMutation.isSuccess
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("testimonialEmail")}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={t("emailPlaceholder")}
                required
                disabled={
                  submitTestimonialMutation.isPending ||
                  submitTestimonialMutation.isSuccess
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label id="rating-label">{t("testimonialRating")}</Label>
            <div
              role="radiogroup"
              aria-labelledby="rating-label"
              className={cn(
                "flex items-center gap-1",
                dir === "rtl" ? "flex-row" : "flex-row-reverse"
              )}
            >
              {[1, 2, 3, 4, 5].map((star) => (
                <label key={star} className="cursor-pointer p-1">
                  <input
                    type="radio"
                    name="star-rating"
                    value={star}
                    checked={star === rating}
                    onChange={() => setRating(star)}
                    disabled={
                      submitTestimonialMutation.isPending ||
                      submitTestimonialMutation.isSuccess
                    }
                    className="sr-only"
                    aria-label={`${star} out of 5 stars`}
                  />
                  <Star
                    size={20}
                    aria-hidden="true"
                    className={cn(
                      "transition-transform hover:scale-110",
                      star <= rating
                        ? "text-amber-400 fill-current"
                        : "text-gray-300"
                    )}
                  />
                </label>
              ))}
              <span
                className={cn(
                  "text-sm text-muted-foreground",
                  dir === "rtl" ? "mr-2" : "ml-2"
                )}
              >
                ({rating}/5)
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t("testimonialMessage")}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              placeholder={t("testimonialPlaceholder")}
              rows={4}
              required
              disabled={
                submitTestimonialMutation.isPending ||
                submitTestimonialMutation.isSuccess
              }
            />
          </div>

          <div className={cn("flex gap-2")}>
            <Checkbox
              id="privacy-agreement"
              checked={privacyAgreed}
              onCheckedChange={(checked) => {
                setPrivacyAgreed(!!checked);
                if (checked) setShowPrivacyError(false);
              }}
              disabled={
                submitTestimonialMutation.isPending ||
                submitTestimonialMutation.isSuccess
              }
            />
            <Label
              htmlFor="privacy-agreement"
              className={cn(
                "text-sm leading-relaxed cursor-pointer",
                showPrivacyError ? "text-red-600" : "text-muted-foreground"
              )}
            >
              {t("privacyPolicyAgreement")}{" "}
              <button
                type="button"
                onClick={handleOpenPrivacyPolicy}
                className="text-primary underline hover:text-primary/80 transition-colors"
              >
                {t("privacyPolicyText")}
              </button>{" "}
              {t("andText")}{" "}
              <button
                type="button"
                onClick={handleOpenTermsOfService}
                className="text-primary underline hover:text-primary/80 transition-colors"
              >
                {t("termsOfServiceText")}
              </button>
            </Label>
          </div>

          {showPrivacyError && (
            <div className="text-red-600 text-sm">
              {t("privacyPolicyRequired")}
            </div>
          )}

          <div>
            <RecaptchaWidget
              hl={language}
              onChange={(token) => setCaptchaToken(token)}
            />
          </div>

          <div className={cn("flex gap-3")}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitTestimonialMutation.isPending}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                submitTestimonialMutation.isPending ||
                submitTestimonialMutation.isSuccess ||
                !privacyAgreed ||
                !captchaToken
              }
            >
              {submitTestimonialMutation.isPending
                ? t("submitting")
                : t("submitTestimonial")}
            </Button>
          </div>
        </form>
      </RTLDialogContent>
    </Dialog>
  );
}
